import React, { useEffect, useState } from "react";
import AuthHeader from "src/components/@auth/auth-layout/auth-header";
import { createFoldername, fetchFiles } from "./action";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import FileManager from ".";
import PrimaryButton from "src/components/reusable/button/primary-button";
import ReactTable from "src/components/reusable/ReactTable";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import TableSearch from "src/components/reusable/table-search";
import { formatDate } from "src/utils/helpers";
import AddIcon from "src/components/icons/common/add-icon";
import { Upload } from "lucide-react";

const CreateFolder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const companyName = location.state?.companyName;
  const [searchParams, setSearchParams] = useSearchParams();

  const [folderName, setFolderName] = useState("");
  const [folderNameError, setFolderNameError] = useState("");
  const [submittingAction, setSubmittingAction] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [folders, setFolders] = useState<any[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(true);

  // 🔹 Fetch folders from API when page loads
  useEffect(() => {
  if (!companyName) return;

  setLoadingFolders(true);
  fetchFiles(companyName, ({ success, data }) => {
    // console.log("🔎 fetchFiles callback:", success, data);

    if (success && data) {
      const mappedFolders = data.map((name: string, idx: number) => ({
        id: idx + 1,
        folder_name: name, // since API only returns string names
        date_created: new Date().toISOString(), // no created date in API
        files_count: 0, // API doesn’t return count, so set 0
      }));
      setFolders(mappedFolders);
    }
    setLoadingFolders(false);
  });
}, [companyName]);


  // 🔎 Filter search
  const filteredFolders = folders.filter((folder) =>
    folder.folder_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Update query param when opening a folder
  const updateQuery = (folderName: string) => {
    setSearchParams({ folder: folderName });
  };

  // ✅ Create new folder
  const handleSubmitCreateFolder = () => {
    if (submittingAction) return;

    let hasError = false;
    if (!folderName.trim()) {
      setFolderNameError("Folder name is required.");
      hasError = true;
    } else if (folderName.length > 50) {
      setFolderNameError("Folder name cannot be more than 50 characters.");
      hasError = true;
    } else {
      setFolderNameError("");
    }

    if (hasError) return;

    setSubmittingAction(true);
    const finalFolderPath = companyName
      ? `${companyName}/${folderName}`
      : folderName;

    createFoldername(finalFolderPath, ({ success, errorType }) => {
      setSubmittingAction(false);
    
      if (errorType === "create") {
        setFolderNameError("Something went wrong. Please try again later.");
        return;
      }
      if (success) {
        const newFolder = {
          id: folders.length + 1,
          folder_name: folderName,
          date_created: new Date().toISOString(),
          files_count: 0,
        };
        setFolders([...folders, newFolder]);
        setFolderName("");
        updateQuery(folderName);
      }
    });
  };

  const handleFolderClick = (folderName: string) => {
    updateQuery(folderName);
  };

  // 🔒 Check Dropbox auth
  useEffect(() => {
    const accessGranted = localStorage.getItem("dropboxAccess");
    if (accessGranted !== "granted") {
      navigate("/dropbox-login", { replace: true });
    }
  }, []);

  // 🏗️ Table setup
  const columnHelper = createColumnHelper<any>();
  const columns: ColumnDef<any>[] = [
    {
      header: "Folder Name",
      accessorKey: "folder_name",
      cell: (item) => (
        <div className="flex items-center">
          <svg
            className="w-4 h-4 text-blue-500 mr-3"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
          </svg>
          <p className="line-clamp-1 font-medium">{item.row.original.folder_name}</p>
        </div>
      ),
    },
    
    {
      header: "Date Created",
      accessorKey: "date_created",
      cell: (item) => (
        <p className="line-clamp-1">{formatDate(item.row.original.date_created)}</p>
      ),
    },
    columnHelper.display({
      header: "Upload Files",
      id: "actions",
      cell: (item) => (
        <div
          className="inline-flex items-center justify-center gap-2 py-1 px-2 bg-primary-900 text-white rounded-md font-semibold cursor-pointer"
          onClick={() => handleFolderClick(item.row.original.folder_name)}
        >
          <button>Click here to uploaded Files</button>
          <Upload strokeWidth={1.25} />
        </div>
      ),
    }),
  ];

  return (
    <>
      <AuthHeader />
      {searchParams.get("folder") ? (
        <FileManager />
      ) : (
        <div className=" w-full z-10 p-6 max-w-6xl mx-auto font-sans min-h-[calc(100vh-88px)]">
          {/* Header */}
          <div className="p-1 pl-0">
            <h6 className="text-lg font-semibold ml-0">Create a new folder to upload files</h6>
          </div>

          {/* Create Folder Section */}
          <div className="mb-4">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Enter folder name"
                  value={folderName}
                  onChange={(e) => {
                    setFolderName(e.target.value);
                    setFolderNameError("");
                  }}
                  className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleSubmitCreateFolder();
                    }
                  }}
                />
                {folderNameError && (
                  <div className="text-red-500 mt-1">{folderNameError}</div>
                )}
              </div>

              <PrimaryButton
                onClick={handleSubmitCreateFolder}
                text="Create Folder"
                loading={submittingAction}
                className="whitespace-nowrap"
                icon={<AddIcon />}
              />
            </div>
          </div>

          {/* Folders Count + Search */}
          <div className="flex items-center gap-1">
            <p className="font-bold text-base">
              Total Folders<span className="ml-3">{folders.length}</span>
            </p>
          </div>

          <div className="flex items-center gap-1 w-full">
            <div className="w-[300px] my-2">
              <TableSearch searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
            </div>
          </div>

          {/* Folders Table */}
          {loadingFolders ? (
            <p className="text-gray-500">Loading folders...</p>
          ) : (
            <ReactTable
              columnsData={columns}
              rowsData={filteredFolders}
              size="medium"
              noTopBorder
            />
          )}
        </div>
      )}
    </>
  );
};

export default CreateFolder;
